import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { Form, Formik } from 'formik';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ExerciseLogoIcon from 'src/assets/img/exercise-logo-icon.svg';
import { Alert } from 'src/components/Alert';
import { BackgroundGradient } from 'src/components/BackgroundGradient';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Container } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { SiteFooter } from 'src/components/SiteFooter';
import { Stack } from 'src/components/Stack';
import { InputAdornment } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { styled } from 'src/components/muiTheme/utils';
import { Collapse } from 'src/components/transitions/Collapse';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { signUpUser } from 'src/models/user/user';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import api from 'src/utils/ajax';
import * as Yup from 'yup';

const BackgroundWrapper = styled('div')(() => ({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '100%',
  height: '100%',
  zIndex: 0,
}));

const PageWrapper = styled('div')(() => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

const COMMON_EMAIL_TYPOS = {
  '.cmo': '.com',
  '.cim': '.com',
  '.con': '.com',
  '.ney': '.net',
  '.nrt': '.net',
  '.og': '.org',
  '.grp': '.org',
} as const;

export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    refreshSessionAndUser,
    user,
    userLoading,
    session,
    sessionLoading,
    isDarkmode,
    disableDarkmode,
    enableDarkmode,
  } = useOptionalSession();

  const [optInMailing, setOptInMailing] = useState(
    session?.optInMailing || false
  );
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [signupsNotAllowed, setSignupsNotAllowed] = useState(false);

  const { t } = useTranslation('translation', { keyPrefix: 'signup' });

  const signupFormik = {
    initialValues: {
      email: new URLSearchParams(location.search).get('email') || '',
      password: '',
      name: '',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(t('email-required'))
        .email(t('invalid-email'))
        .transform((value) => value.replace(/\s+/g, ''))
        .test('email-typo', (value, { createError }) => {
          if (!value) return true;
          const domain = value.split('@')[1];
          if (!domain) return true;

          for (const [typo, correction] of Object.entries(COMMON_EMAIL_TYPOS)) {
            if (domain.endsWith(typo)) {
              return createError({
                message: t('email-typo-suggestion', {
                  corrected: value.replace(typo, correction),
                }),
              });
            }
          }
          return true;
        }),
      name: Yup.string()
        .required(t('name-required'))
        .trim()
        .matches(/^.+\s+.+$/, t('both-names'))
        .min(3, t('name-min-length')),
      password: Yup.string().required(t('password-required')),
    }),
    onSubmit: async (values) => {
      try {
        await signUpUser(
          values.email,
          values.password,
          values.name,
          optInMailing,
          values.timeZone
        );
        await refreshSessionAndUser();

        window.setTimeout(() => {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            navigate(redirectPath);
          }
        }, 2000);
      } catch (e) {
        setError(api.formatError(e, t('signup-error')));
      }
    },
  };

  const darkMode = isDarkmode();
  const logoUrl =
    darkMode && session?.darkmodeLogoUrl
      ? session.darkmodeLogoUrl
      : session?.logoUrl || ExerciseLogoIcon;

  const logoContainer = (
    <Container
      maxWidth="lg"
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          mb: 4,
          mt: 4,
        },
        mb: 6,
        mt: 8,
        position: 'relative',
      })}
    >
      <Stack display="flex" justifyContent="space-between" alignItems="center">
        {!sessionLoading && session && (
          <img
            src={logoUrl}
            style={{ maxHeight: '80px' }}
            alt={`${session?.brand || 'Exercise'} Logo`}
          />
        )}
      </Stack>
    </Container>
  );

  useEffect(() => {
    const state = location.state as {
      unauthorizedMessage?: string;
      from?: string;
    };
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = searchParams.get('redirect_to');

    if (state) {
      if (state.unauthorizedMessage) setError(state.unauthorizedMessage);
      if (state.from && !redirectTo) setRedirectPath(state.from);

      // Clear the state to prevent showing the message on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }

    // Prioritize redirect_to query parameter over location state
    if (redirectTo) {
      setRedirectUrl(redirectTo);
    } else if (!session?.id) {
      setRedirectUrl('/dashboard/');
    }
  }, [location, navigate, session]);

  useEffect(() => {
    if (!userLoading && user?.id) {
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [user, userLoading, navigate, redirectPath, redirectUrl]);

  // Add new effect to handle signup restrictions
  useEffect(() => {
    if (session && !session.freeSignups) {
      setSignupsNotAllowed(true);
      const searchParams = new URLSearchParams(location.search);
      const queryString = searchParams.toString();
      const loginPath = `/login${queryString ? `?${queryString}` : ''}`;

      navigate(loginPath, { replace: true });
    }
  }, [session, location.search, navigate]);

  useLayoutEffect(() => {
    disableDarkmode();
    return () => {
      enableDarkmode();
    };
  }, [disableDarkmode, enableDarkmode]);

  if (session && !session?.freeSignups) {
    return (
      <>
        <Meta title={t('sign-up', 'Sign Up')} brand={session?.brand} />
        <DrawerLayout drawerOpen={false}>
          <BackgroundWrapper>
            <BackgroundGradient color={error ? 'error' : 'primary'} />
          </BackgroundWrapper>

          <PageWrapper>
            <Box sx={{ flex: '1 0 auto', pb: 5 }}>
              {logoContainer}
              <Container maxWidth="sm" sx={{ position: 'relative' }}>
                <Stack
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h4" mb={4}>
                    {t('sign-up', 'Sign Up')}
                  </Typography>
                </Stack>
                <Card
                  sx={{ p: 2.5, bgcolor: 'transparent', boxShadow: 'none' }}
                >
                  <Collapse in={signupsNotAllowed} collapsedSize={0}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {t('signups-not-allowed')}
                    </Alert>
                  </Collapse>
                </Card>
              </Container>
            </Box>
            <SiteFooter />
          </PageWrapper>
        </DrawerLayout>
      </>
    );
  }

  return (
    <>
      <Meta title={t('sign-up', 'Sign Up')} brand={session?.brand} />
      <DrawerLayout drawerOpen={false}>
        <BackgroundWrapper>
          <BackgroundGradient color={error ? 'error' : 'primary'} />
        </BackgroundWrapper>

        <PageWrapper>
          <Box sx={{ flex: '1 0 auto', pb: 5 }}>
            {logoContainer}
            <Container maxWidth="sm" sx={{ position: 'relative' }}>
              <Card sx={{ p: 2.5, bgcolor: 'transparent', boxShadow: 'none' }}>
                <Stack
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h4" mb={4}>
                    {t('sign-up', 'Sign Up')}
                  </Typography>
                </Stack>

                <Collapse in={!!error} collapsedSize={0}>
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                </Collapse>

                <Formik enableReinitialize={true} {...signupFormik}>
                  {({ isSubmitting, isValid, dirty }) => (
                    <Form>
                      <FormTextField
                        sx={{ mt: 0 }}
                        label={t('email')}
                        name="email"
                      />
                      <FormTextField
                        sx={{ mt: 3 }}
                        label={t('name')}
                        name="name"
                      />
                      <FormTextField
                        sx={{ mt: 3 }}
                        label={t('password')}
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              <Icon
                                size="small"
                                icon={showPassword ? faEyeSlash : faEye}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {session?.hasMailingList && (
                        <Checkbox
                          sx={{ mt: 3 }}
                          label={session?.mailingList || t('mailing-list')}
                          checked={optInMailing}
                          onChange={(event) =>
                            setOptInMailing(event.target.checked)
                          }
                        />
                      )}

                      <Button
                        variant="prominent"
                        sx={{ mt: 3 }}
                        disabled={isSubmitting || !isValid || !dirty}
                        loading={isSubmitting}
                        type="submit"
                        fullWidth
                      >
                        {t('sign-up', 'Sign Up')}
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Card>

              <Typography
                sx={{ mt: 5, position: 'relative' }}
                align="center"
                variant="body1"
                color="text.secondary"
              >
                {t('have-account')}
                <br />
                <Link component={RouterLink} to={`/login`}>
                  {t('sign-in')}
                </Link>
              </Typography>

              <Typography
                sx={{ mt: 4, mb: 2, position: 'relative' }}
                align="center"
                variant="body2"
                color="text.secondary"
              >
                {session?.id
                  ? t('agree-to-terms-with-brand')
                  : t('agree-to-terms')}{' '}
                <Link href="/terms-of-service" target="_blank">
                  {' '}
                  {t('terms-of-service')}
                </Link>{' '}
                {t('and')}{' '}
                <Link href="/privacy-policy" target="_blank">
                  {t('privacy-policy')}
                </Link>
                . {/* final dot */}
              </Typography>
            </Container>
          </Box>
          <SiteFooter />
        </PageWrapper>
      </DrawerLayout>
    </>
  );
};
